<template>
  <Breadcrumb></Breadcrumb>
  <div v-if="rentalListingAgreement">
    <div class="grid grid-cols-3 items-start gap-4">
      <div class="col-span-2 space-y-4">
        <!-- Title block -->
        <DetailCard>
          <template #default>
            <div class="flex gap-2 items-center">
              <div class="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center">
                <BriefcaseIcon class="h-8 w-8 text-gray-700"></BriefcaseIcon>
              </div>
              <RentalListingAgreementId :rentalListingAgreement="rentalListingAgreement" />
              <div>
                <RiskScoringLabel :level="riskScoring?.attributes?.scoreLevel ?? undefined" />
              </div>
              <router-link class="text-indigo-600 hover:text-indigo-900 ml-auto transition-transform duration-200 hover:scale-110" :to="{ name: 'edit-rental-listing-agreement', params: { id: rentalListingAgreement.id } }">
                {{ $gettext("Editer") }}<span class="sr-only">, {{ rentalListingAgreement.id }}</span>
              </router-link>
            </div>
          </template>
        </DetailCard>

        <HighRiskWarningBanner
          v-if="riskScoring?.attributes?.scoreLevel === 'high' && !riskScoring?.attributes?.reviewApproval"
        />

        <!-- Risk scoring factors -->
        <DetailCard>
          <template #default>
            <RiskScoringFactorList
              v-if="riskScoring"
              :riskScoring="riskScoring"
              :riskScoringStore="riskScoringStore"
              :requireHighRiskConfirmation="true"
              :organization="organization"
              @updated="handleRiskScoringUpdate"
            />
          </template>
        </DetailCard>
      </div>

      <div class="col-span-1 flex flex-col gap-4">
        <!-- Chart -->
        <DetailCard :title="$gettext('Aperçu')">
          <template #footer>
            <RiskScoringChart v-if="riskScoring" :riskScoring="riskScoring" />
          </template>
        </DetailCard>

        <!-- Sanctions -->
        <DetailCard v-if="riskScoring">
          <SanctionsBox :riskScoring="riskScoring" />
        </DetailCard>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from "vue";
import { useRentalListingAgreementStore } from "@/stores/rentalListingAgreement.js";
import { useRoute } from "vue-router";
import RiskScoringLabel from "@/components/risk-scorings/RiskScoringLabel.vue";
import RiskScoringFactorList from "@/components/risk-scorings/RiskScoringFactorList.vue";
import { BriefcaseIcon } from "@heroicons/vue/24/outline";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { PieChart } from "echarts/charts";
import { useRiskScoring } from "@/stores/riskScoring";
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent
} from "echarts/components";
import Breadcrumb from "@/components/layout/BreadcrumbNavigation.vue";
import RiskScoringChart from "@/components/risk-scorings/RiskScoringChart.vue";
import SanctionsBox from "@/components/risk-scorings/SanctionsBox.vue";
import RentalListingAgreementId from "@/components/rental-listing-agreements/RentalListingAgreementId.vue";
import { DetailCard } from "@/components/ui";
import HighRiskWarningBanner from "@/components/risk-scorings/HighRiskWarningBanner.vue";
import { useOrganizationStore } from "@/stores/organization.js";

use([
  CanvasRenderer,
  PieChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent
]);

const route = useRoute();
const rentalListingAgreementId = route.params.id;
const rentalListingAgreementStore = useRentalListingAgreementStore();
const rentalListingAgreement = ref(null);

const riskScoringStore = useRiskScoring("rental-listing-agreements", rentalListingAgreementId);
const riskScoring = ref(null);
const organizationStore = useOrganizationStore();
const organization = ref(null);

const loadRentalListingsAgreement = async () => {
  rentalListingAgreement.value = await rentalListingAgreementStore.fetchOne(rentalListingAgreementId, null, ["lastRiskScoring"]);
  const orgId = rentalListingAgreement.value?.attributes?.organizationId;
  if (orgId && !organization.value) {
    organization.value = await organizationStore.fetchOne(orgId);
  }
};

const handleRiskScoringUpdate = async () => {
  await loadRentalListingsAgreement();
  let riskScoringId = rentalListingAgreement.value.relationships.lastRiskScoring.data.id;
  riskScoring.value = await riskScoringStore.fetchOne(riskScoringId);
};

onMounted(async () => {
  await loadRentalListingsAgreement();
  let riskScoringId = rentalListingAgreement.value.relationships.lastRiskScoring.data.id;
  riskScoring.value = await riskScoringStore.fetchOne(riskScoringId);
});
</script>
